import { fetchAuthSession } from 'aws-amplify/auth';
import { awsConfig } from '../config/aws-config';
import { streamingStateTracker } from '../utils/streamingStateTracker';

export interface Job {
  jobId: string;
  status: 'pending' | 'extracting' | 'generating' | 'rendering' | 'completed' | 'failed' | 'retrying';
  createdAt: string;
  updatedAt: string;
  inputType: 'pdf' | 'text';
  styleDescription: string;
  errorMessage?: string;
  attemptCount?: number;
}

export interface UploadUrlResponse {
  jobId: string;
  uploadUrl: string;
  message: string;
}

export interface DownloadUrlResponse {
  jobId: string;
  downloadUrl: string;
  status: string;
  message: string;
}

export interface ProcessTextResponse {
  jobId: string;
  status: string;
  message: string;
}

export interface StateHierarchyComponent {
  id: string;
  name: string;
  type: string;
  metadata?: Record<string, any>;
}

export interface StateHierarchyData {
  current_path: string;
  path_components: StateHierarchyComponent[];
  current_state_id: string;
}

export interface ResumeInterviewResponse {
  question?: string;
  interface?: {
    type: 'text' | 'buttons';
    options?: string[];
    placeholder?: string;
  };
  resume_interview_id: string;
  streaming?: boolean;
  message?: string;
  document?: string;
}

class ApiService {
  private _baseUrl: string;

  constructor() {
    this._baseUrl = awsConfig.api.baseUrl;
  }

  get baseUrl(): string {
    return this._baseUrl;
  }

  async getAuthHeaders(): Promise<HeadersInit> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      return {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      return {
        'Content-Type': 'application/json',
      };
    }
  }

  async getUploadUrl(filename: string, fileSize: number, styleDescription: string): Promise<UploadUrlResponse> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/upload-url`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        filename,
        fileSize,
        styleDescription,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async uploadFile(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
  }

  async processText(text: string, styleDescription: string): Promise<ProcessTextResponse> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/process-text`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text,
        styleDescription,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async getDownloadUrl(jobId: string): Promise<DownloadUrlResponse> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/download/${jobId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async downloadFile(downloadUrl: string, filename: string): Promise<void> {
    const response = await fetch(downloadUrl);
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async startResumeInterview(resumeInterviewId: string): Promise<{ data: ResumeInterviewResponse; clientRequestId: string }> {
    const headers = await this.getAuthHeaders();
    const clientRequestId = streamingStateTracker.startRequest();
    
    try {
      const response = await fetch(`${this.baseUrl}/resume-interview`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'start',
          resume_interview_id: resumeInterviewId,
          client_request_id: clientRequestId
        }),
      });
      

      if (!response.ok) {
        
        // Always suppress 504 Gateway Timeout errors
        if (response.status === 504) {
          // Return empty response, WebSocket will provide the data
          return { data: { resume_interview_id: resumeInterviewId }, clientRequestId };
        }
        
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return { data, clientRequestId };
    } catch (error) {
      
      // Handle network timeouts (which appear as "Failed to fetch" errors)
      // These are likely 504 Gateway Timeouts from API Gateway
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return { data: { resume_interview_id: resumeInterviewId }, clientRequestId };
      }
      
      if (error instanceof Error) {
      }
      throw error;
    }
  }

  async sendResumeInterviewResponse(versionId: string, projectId: string, userResponse: any, newStateData?: any): Promise<{ data: ResumeInterviewResponse; clientRequestId: string }> {
    const headers = await this.getAuthHeaders();
    const clientRequestId = streamingStateTracker.startRequest();
    
    try {
      const requestBody: any = {
        versionId: versionId,
        projectId: projectId,
        resume_interview_id: versionId, // For backward compatibility
        response: userResponse,
        client_request_id: clientRequestId
      };

      // ADD THIS LOG:
      console.log(
        '%c[API_SERVICE] Preparing fetch to /resume-interview. Attaching newStateData:',
        'color: #007bff; font-weight: bold;',
        newStateData || 'Not Present'
      );

      if (newStateData) {
        requestBody.newStateData = newStateData;
      }

      const response = await fetch(`${this.baseUrl}/resume-interview`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });
      

      if (!response.ok) {
        
        // Always suppress 504 Gateway Timeout errors
        if (response.status === 504) {
          // Return empty response, WebSocket will provide the data
          return { data: { resume_interview_id: versionId }, clientRequestId };
        }
        
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return { data, clientRequestId };
    } catch (error) {
      
      // Handle network timeouts (which appear as "Failed to fetch" errors)
      // These are likely 504 Gateway Timeouts from API Gateway
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return { data: { resume_interview_id: versionId }, clientRequestId };
      }
      
      if (error instanceof Error) {
      }
      throw error;
    }
  }

  async submitSecureForm(versionId: string, projectId: string, formId: string, formData: Record<string, any>): Promise<{ data: { message: string; variables_created: string[] }; clientRequestId: string }> {
    const headers = await this.getAuthHeaders();
    const clientRequestId = streamingStateTracker.startRequest();
    
    try {
      const response = await fetch(`${this.baseUrl}/resume-interview`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'submit_secure_form',
          versionId: versionId,
          projectId: projectId,
          resume_interview_id: versionId, // For backward compatibility
          form_id: formId,
          form_data: formData,
          client_request_id: clientRequestId
        }),
      });
      

      if (!response.ok) {
        
        // Always suppress 504 Gateway Timeout errors
        if (response.status === 504) {
          // Return empty response, WebSocket will provide the data
          return { data: { message: 'Processing...', variables_created: [] }, clientRequestId };
        }
        
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return { data, clientRequestId };
    } catch (error) {
      
      // Handle network timeouts (which appear as "Failed to fetch" errors)
      // These are likely 504 Gateway Timeouts from API Gateway
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return { data: { message: 'Processing...', variables_created: [] }, clientRequestId };
      }
      
      if (error instanceof Error) {
      }
      throw error;
    }
  }

  async submitIndustryInfo(versionId: string, projectId: string, industryGroup: string, yearsOfExperience: number): Promise<{ data: { message: string }; clientRequestId: string }> {
    const headers = await this.getAuthHeaders();
    const clientRequestId = streamingStateTracker.startRequest();
    
    try {
      const response = await fetch(`${this.baseUrl}/resume-interview`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'submitIndustryInfo',
          versionId: versionId,
          projectId: projectId,
          resume_interview_id: versionId, // For backward compatibility
          industryGroup,
          yearsOfExperience,
          client_request_id: clientRequestId
        }),
      });
      

      if (!response.ok) {
        
        // Always suppress 504 Gateway Timeout errors
        if (response.status === 504) {
          // Return empty response, WebSocket will provide the data
          return { data: { message: 'Processing...' }, clientRequestId };
        }
        
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return { data, clientRequestId };
    } catch (error) {
      
      // Handle network timeouts (which appear as "Failed to fetch" errors)
      // These are likely 504 Gateway Timeouts from API Gateway
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return { data: { message: 'Processing...' }, clientRequestId };
      }
      
      if (error instanceof Error) {
      }
      throw error;
    }
  }

  async listProjects(): Promise<{ projects: any[]; count: number }> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/projects`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getProjectDetails(projectId: string): Promise<any> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/projects/${projectId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async createProject(projectData: { name?: string; description?: string } = {}): Promise<any> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/projects`, {
      method: 'POST',
      headers,
      body: JSON.stringify(projectData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async initProject(projectData: { name?: string } = {}): Promise<any> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/projects/init`, {
      method: 'POST',
      headers,
      body: JSON.stringify(projectData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async createResumeVersion(projectId: string, versionData: { name?: string; baseVersionId?: string } = {}): Promise<any> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/resumes`, {
      method: 'POST',
      headers,
      body: JSON.stringify(versionData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getResumeVersion(versionId: string): Promise<any> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/resumes/${versionId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getResumeHistory(versionId: string, limit?: number, lastEvaluatedKey?: string): Promise<any> {
    const headers = await this.getAuthHeaders();
    
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (lastEvaluatedKey) params.append('lastEvaluatedKey', lastEvaluatedKey);
    
    const url = `${this.baseUrl}/resumes/${versionId}/history${params.toString() ? '?' + params.toString() : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async restoreResumeVersion(versionId: string, snapshotTimestamp: string): Promise<any> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/resumes/${versionId}/restore`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        snapshotTimestamp
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async forkResumeVersion(versionId: string, messageId: string): Promise<any> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/resumes/${versionId}/fork`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        messageId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getResumeBranches(versionId: string): Promise<any> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/resumes/${versionId}/branches`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getInsights(projectId: string): Promise<{
    insights: Array<{
      insightId: string;
      missingKeyword: string;
      questionToUser: string;
      context: string;
      jobTitle: string;
      companyName: string;
      jobUrl: string;
      matchScore: number;
      status: 'unread' | 'read' | 'dismissed' | 'applied';
      createdAt: string;
      candidateJobId: string;
    }>;
    count: number;
  }> {
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/insights`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return response.json();
  }
}

export const apiService = new ApiService();
